import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { FaImage, FaTimes, FaSpinner } from 'react-icons/fa';
import api from '@/utils/api';
import { getMediaUrl } from '@/constants/media';
import './ImageUpload.scss';

interface ImageUploadProps {
  onUploadComplete: (url: string) => void;
  currentImage?: string;
  endpoint: 'avatar' | 'post-image' | 'banner';
  fieldName: 'avatar' | 'image' | 'banner';
  maxSizeMB?: number;
  className?: string;
  /** Skip the built-in "Choose Image" button when the parent owns the
   *  trigger (paperclip in PostComposer). Preview + remove still
   *  render normally once a file is picked. */
  hideDefaultTrigger?: boolean;
}

export interface ImageUploadHandle {
  /** Open the native file picker from the parent — bypasses the
   *  built-in button so the composer's paperclip is a one-click flow. */
  openPicker: () => void;
}

const ImageUpload = forwardRef<ImageUploadHandle, ImageUploadProps>(({
  onUploadComplete,
  currentImage,
  endpoint,
  fieldName,
  maxSizeMB = 20, // Increased to 20MB to support high-res photos and HEIC files
  className = '',
  hideDefaultTrigger = false,
}, ref) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    currentImage ? getMediaUrl(currentImage) : null
  );
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset error
    setError(null);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setError(`File size must be less than ${maxSizeMB}MB`);
      return;
    }

    // Check if file is HEIC/HEIF (browsers can't preview these)
    const isHeic = file.type === 'image/heic' || file.type === 'image/heif';

    // Show preview for non-HEIC files (browsers can render them)
    if (!isHeic) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }

    // Upload file
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append(fieldName, file);

      const response = await api.post(`/upload/${endpoint}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.url) {
        // For HEIC files or any uploaded image, show the converted/uploaded image from server
        const fullUrl = getMediaUrl(response.data.url);
        setPreviewUrl(fullUrl);
        onUploadComplete(response.data.url);
      } else {
        throw new Error('No URL returned from server');
      }
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || 'Failed to upload image');
      setPreviewUrl(currentImage ? getMediaUrl(currentImage) : null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    setError(null);
    onUploadComplete('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  // Expose the picker trigger to the parent so the composer can hand
  // the paperclip icon off to it without rendering the built-in
  // "Choose Image" button. See PostComposer.
  useImperativeHandle(ref, () => ({
    openPicker: () => fileInputRef.current?.click(),
  }), []);

  return (
    <div className={`image-upload ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={isUploading}
      />

      {isUploading && !previewUrl ? (
        // Show loading state for HEIC files (no browser preview available)
        <div className="upload-loading">
          <FaSpinner className="spinner" />
          <span>Uploading & Converting...</span>
        </div>
      ) : previewUrl ? (
        // Show preview with optional upload overlay
        <div className="image-preview">
          <img src={previewUrl} alt="Preview" />
          {!isUploading && (
            <button
              type="button"
              className="remove-btn"
              onClick={handleRemove}
              title="Remove image"
            >
              <FaTimes />
            </button>
          )}
          {isUploading && (
            <div className="upload-overlay">
              <FaSpinner className="spinner" />
              <span>Uploading...</span>
            </div>
          )}
        </div>
      ) : hideDefaultTrigger ? (
        // Parent owns the trigger — render nothing in the empty
        // state so we don't double-up buttons.
        null
      ) : (
        // Show upload button
        <button
          type="button"
          className="upload-btn"
          onClick={handleClick}
          disabled={isUploading}
        >
          <FaImage />
          <span>Choose Image</span>
        </button>
      )}

      {error && <div className="error-message">{error}</div>}
    </div>
  );
});

ImageUpload.displayName = 'ImageUpload';

export default ImageUpload;
